/**
 * Landing Page - Clinafy Institutional Website
 *
 * Main landing page with all marketing sections:
 * - Hero with CTA
 * - Social Proof
 * - Features Preview
 * - Pricing Cards
 * - Testimonials
 * - FAQ
 * - Final CTA
 */

import React, { useState } from 'react';
import { useSEO, SEO_CONFIG } from '../../hooks/useSEO';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Rating,
  Stack,
  alpha,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VideocamIcon from '@mui/icons-material/Videocam';
import GroupsIcon from '@mui/icons-material/Groups';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StarIcon from '@mui/icons-material/Star';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import DiamondIcon from '@mui/icons-material/Diamond';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LockIcon from '@mui/icons-material/Lock';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PsychologyIcon from '@mui/icons-material/Psychology';
import QueueIcon from '@mui/icons-material/Queue';
import MicIcon from '@mui/icons-material/Mic';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ShareIcon from '@mui/icons-material/Share';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

// Components
import Navbar from '../../components/landing/Navbar';
import Footer from '../../components/landing/Footer';
import HeroSlideshow from '../../components/landing/HeroSlideshow';
import ExitIntentPopup from '../../components/landing/ExitIntentPopup';
import LeadMagnetModal from '../../components/landing/LeadMagnetModal';
// LandingThemeProvider removed - using explicit bgcolor on Cards instead
import { landingColors, landingGradients, landingShadows } from '../../theme/landingTheme';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShieldIcon from '@mui/icons-material/Shield';
import CloudIcon from '@mui/icons-material/Cloud';
import BackupIcon from '@mui/icons-material/Backup';

// Plan config
import { PLAN_DEFINITIONS } from '../../config/plans';

// Analytics
import {
  trackTrialStart,
  trackDemoRequest,
  buildSignupUrl,
} from '../../utils/analytics';
import VideoDemoSection from '../../components/landing/VideoDemoSection';

// Screenshots config
import { HERO_SLIDES } from '../../config/screenshots';

// Plan icons for display
const planIcons: Record<string, React.ReactNode> = {
  individual: <PersonIcon sx={{ fontSize: 24 }} />,
  small_clinic: <GroupsIcon sx={{ fontSize: 24 }} />,
  clinic_pro: <WorkspacePremiumIcon sx={{ fontSize: 24 }} />,
  enterprise: <DiamondIcon sx={{ fontSize: 24 }} />,
};

// ============================================================================
// Data
// ============================================================================

const socialProofStats = [
  { value: '+500', label: 'Profissionais' },
  { value: '+50.000', label: 'Consultas' },
  { value: '4.9/5', label: 'Avaliação' },
  { value: '<2h', label: 'Suporte' },
];

const whyChooseItems = [
  {
    icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
    title: 'Economize 10h/semana',
    description: 'Automatize prontuários, lembretes e financeiro. Mais tempo para seus pacientes.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: 'Segurança Profissional',
    description: 'Criptografia ponta-a-ponta, backup automático, conformidade total com LGPD.',
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 40 }} />,
    title: 'Suporte Humano',
    description: 'Resposta em até 2 horas por pessoas reais. Nada de robôs ou filas.',
  },
];

// Featured AI section (highlighted)
const aiFeature = {
  icon: <AutoAwesomeIcon />,
  title: 'Inteligência Artificial',
  subtitle: 'O futuro da saúde mental clínica. Hoje.',
  description: 'Transcrição automática de sessões, resumos inteligentes e detecção de padrões emocionais. Foque no paciente enquanto a IA cuida da documentação.',
  tag: 'EXCLUSIVO',
  tagColor: '#1B6A63',
  bullets: [
    'Transcrição automática de videochamadas',
    'Resumos inteligentes de cada sessão',
    'Detecção de padrões emocionais',
    'Chat com IA sobre o caso clínico',
  ],
};

const features = [
  {
    icon: <DescriptionIcon />,
    title: 'Prontuário Eletrônico Completo',
    description: 'Histórico completo do paciente, evoluções, anexos e documentos em um só lugar. Conforme regulamentações profissionais.',
    tag: 'Essencial',
    tagColor: landingColors.primary.main,
  },
  {
    icon: <CalendarMonthIcon />,
    title: 'Agenda Inteligente',
    description: 'Lembretes automáticos via WhatsApp e email. Reduza faltas em até 40%.',
    tag: 'Automação',
    tagColor: landingColors.accent.teal,
  },
  {
    icon: <EventAvailableIcon />,
    title: 'Agendamento Online',
    description: 'Página pública para pacientes solicitarem horários. Você aprova e o paciente é notificado automaticamente.',
    tag: 'NOVO',
    tagColor: '#1B6A63',
  },
  {
    icon: <AccountCircleIcon />,
    title: 'Portal do Paciente',
    description: 'Seus pacientes acessam consultas, documentos e confirmam presença. Login seguro via magic link.',
    tag: 'NOVO',
    tagColor: '#1B6A63',
  },
  {
    icon: <VideocamIcon />,
    title: 'Teleconsulta HD',
    description: 'Videochamadas integradas com sala de espera virtual e link automático para pacientes.',
    tag: 'Vídeo HD',
    tagColor: landingColors.status.info,
  },
  {
    icon: <DashboardIcon />,
    title: 'Dashboard Financeiro',
    description: 'Visão completa de faturamento, consultas realizadas, taxa de presença e receita.',
    tag: 'Analytics',
    tagColor: landingColors.accent.orange,
  },
  {
    icon: <GroupsIcon />,
    title: 'Gestão de Equipe',
    description: 'Múltiplos profissionais com permissões personalizadas e agenda individual.',
    tag: 'Escalável',
    tagColor: landingColors.status.warning,
  },
  {
    icon: <LockIcon />,
    title: 'Segurança Total',
    description: 'Criptografia AES-256, backup automático, conformidade LGPD e servidores no Brasil.',
    tag: 'Protegido',
    tagColor: landingColors.neutral.gray600,
  },
  {
    icon: <AssignmentIcon />,
    title: 'Documentos Clínicos',
    description: 'Gere recibos, declarações e relatórios automaticamente. Templates personalizáveis.',
    tag: 'Automático',
    tagColor: landingColors.accent.teal,
  },
  {
    icon: <PsychologyIcon />,
    title: 'Avaliações Clínicas',
    description: 'Aplique instrumentos padronizados, acompanhe a evolução e visualize gráficos.',
    tag: 'Estruturado',
    tagColor: landingColors.status.info,
  },
  {
    icon: <QueueIcon />,
    title: 'Fila de Espera',
    description: 'Gerencie lista de espera com notificações automáticas quando vagar horário.',
    tag: 'Organizado',
    tagColor: landingColors.accent.orange,
  },
  {
    icon: <MicIcon />,
    title: 'Voz para Texto',
    description: 'Transcreva notas de sessão por voz em tempo real. Documentação hands-free.',
    tag: 'IA',
    tagColor: '#1B6A63',
  },
  {
    icon: <TrendingUpIcon />,
    title: 'Dashboard de Progresso',
    description: 'Acompanhe a evolução dos pacientes com métricas visuais e tendências.',
    tag: 'Analytics',
    tagColor: landingColors.status.success,
  },
];

interface Testimonial {
  name: string;
  role: string;
  location: string;
  rating: number;
  text: string;
  // Optional real photo URL. When set, Avatar renders the image; otherwise
  // the initial letter is used as fallback. Add a URL here once you have
  // a customer photo with permission to publish.
  photoUrl?: string;
}

const testimonials: Testimonial[] = [
  {
    name: 'Dra. Ana Carolina Silva',
    role: 'Psicóloga Clínica',
    location: 'São Paulo, SP',
    rating: 5,
    text: 'Antes eu perdia 2 horas por dia com papelada. Agora a Clinafy faz tudo automático. Sobra tempo para minha família e para estudar.',
  },
  {
    name: 'Dr. Ricardo Mendes',
    role: 'Terapeuta Organizacional',
    location: 'Rio de Janeiro, RJ',
    rating: 5,
    text: 'A transcrição por IA mudou minha vida. Posso focar 100% no paciente sabendo que tudo está sendo registrado com precisão.',
  },
  {
    name: 'Dra. Mariana Costa',
    role: 'Terapeuta Infantil',
    location: 'Belo Horizonte, MG',
    rating: 5,
    text: 'Migrei de outro sistema e não me arrependo. Interface muito mais moderna e o suporte responde de verdade, em minutos!',
  },
];

const faqItems = [
  {
    question: 'Meus dados estão seguros?',
    answer: 'Sim! Utilizamos criptografia AES-256 para todos os dados, servidores AWS localizados no Brasil, backups automáticos a cada 5 minutos e conformidade total com a LGPD. Seus dados de pacientes estão mais seguros conosco do que em arquivos físicos ou planilhas.',
  },
  {
    question: 'Posso migrar meus dados de outro sistema?',
    answer: 'Sim! Oferecemos migração gratuita assistida para todos os planos pagos. Nossa equipe ajuda a importar seus pacientes, histórico de sessões e dados financeiros de qualquer sistema anterior.',
  },
  {
    question: 'O que acontece se eu cancelar?',
    answer: 'Você pode cancelar a qualquer momento, sem multas ou taxas. Seus dados ficam disponíveis para exportação por 30 dias após o cancelamento. Não acreditamos em prender clientes por contrato.',
  },
  {
    question: 'Preciso instalar alguma coisa?',
    answer: 'Não! A Clinafy funciona 100% no navegador. Basta acessar pelo computador, tablet ou celular. Não precisa baixar nenhum aplicativo ou software.',
  },
  {
    question: 'Como funciona o suporte?',
    answer: 'Oferecemos suporte por email, WhatsApp e chat. Tempo médio de resposta: menos de 2 horas em horário comercial. Para planos Enterprise, temos suporte prioritário 24/7.',
  },
];

// ============================================================================
// Component
// ============================================================================

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);
  const [leadMagnetOpen, setLeadMagnetOpen] = useState(false);

  // SEO - Dynamic meta tags
  useSEO(SEO_CONFIG.home);

  const handleFaqChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ bgcolor: landingColors.background.default }}>
      <Navbar />

      <Box component="main" id="main-content">
      {/* ================================================================== */}
      {/* HERO SECTION */}
      {/* ================================================================== */}
      <Box
        sx={{
          background: landingGradients.hero,
          pt: { xs: 12, md: 16 }, // Extra padding for navbar overlay
          pb: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
          // Decorative elements
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.03)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h1"
                sx={{
                  color: landingColors.neutral.white,
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 700,
                  lineHeight: 1.1,
                  mb: 3,
                  letterSpacing: '-0.02em',
                }}
              >
                Sua clínica de saúde mental merece a tecnologia do futuro.{' '}
                <Box component="span" sx={{ color: landingColors.accent.yellow }}>
                  Hoje.
                </Box>
              </Typography>

              <Typography
                variant="h5"
                component="h2"
                sx={{
                  color: alpha(landingColors.neutral.white, 0.9),
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: 600,
                }}
              >
                Prontuário eletrônico com IA, agenda inteligente, teleconsulta integrada
                e gestão financeira completa. Tudo em um só lugar.
              </Typography>

              {/* CTA Buttons */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 5 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    trackTrialStart('hero');
                    navigate(buildSignupUrl('/signup', { trial: 'true' }));
                  }}
                  sx={{
                    bgcolor: landingColors.neutral.white,
                    color: landingColors.primary.main,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: landingShadows.button,
                    '&:hover': {
                      bgcolor: landingColors.neutral.gray100,
                      boxShadow: landingShadows.buttonHover,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Testar Grátis por 30 Dias
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    trackDemoRequest('hero');
                    navigate('/funcionalidades');
                  }}
                  sx={{
                    borderColor: landingColors.neutral.white,
                    color: landingColors.neutral.white,
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: landingColors.neutral.white,
                      bgcolor: alpha(landingColors.neutral.white, 0.1),
                    },
                  }}
                >
                  Ver Demonstração
                </Button>
              </Box>

              <VideoDemoSection variant="hero" />

              {/* Social Proof Stats */}
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 3, md: 4 },
                  flexWrap: 'wrap',
                }}
              >
                {socialProofStats.map((stat) => (
                  <Box key={stat.label}>
                    <Typography
                      sx={{
                        color: landingColors.neutral.white,
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        lineHeight: 1,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        color: alpha(landingColors.neutral.white, 0.7),
                        fontSize: '0.875rem',
                        mt: 0.5,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Hero Slideshow */}
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'block', height: { xs: '200px', sm: '300px', md: '400px' } }}>
              <HeroSlideshow
                slides={HERO_SLIDES}
                autoPlay={true}
                autoPlayInterval={4000}
                height={420}
                borderRadius={16}
                showControls={true}
                showDots={true}
                showCaptions={true}
                showPlayPause={true}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* WHY CHOOSE SECTION */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: landingColors.background.alt }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 2,
            }}
          >
            Por que escolher a Clinafy?
          </Typography>
          <Typography
            align="center"
            sx={{
              color: landingColors.neutral.gray600,
              fontSize: '1.1rem',
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Desenvolvida por profissionais de saúde mental, para profissionais de saúde mental. Entendemos suas necessidades.
          </Typography>

          <Grid container spacing={4}>
            {whyChooseItems.map((item, index) => (
              <Grid key={index} size={{ xs: 12, md: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 4,
                    borderRadius: 3,
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${landingColors.neutral.gray200}`,
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: landingShadows.card,
                      transform: 'translateY(-4px)',
                      borderColor: landingColors.primary.light,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: alpha(landingColors.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      color: landingColors.primary.main,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      color: landingColors.neutral.gray900,
                      mb: 2,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: landingColors.neutral.gray600, lineHeight: 1.7 }}>
                    {item.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* FEATURES SECTION */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 2,
            }}
          >
            Tudo que você precisa para sua clínica prosperar
          </Typography>
          <Typography
            align="center"
            sx={{
              color: landingColors.neutral.gray600,
              fontSize: '1.1rem',
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Funcionalidades pensadas para simplificar seu dia a dia
          </Typography>

          {/* AI HIGHLIGHT CARD - Differentiator */}
          <Card
            sx={{
              mb: 4,
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, #1B6A63 0%, #27AB83 50%, #1F9972 100%)',
              border: 'none',
              boxShadow: '0 20px 40px rgba(27, 106, 99, 0.3)',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 25px 50px rgba(27, 106, 99, 0.4)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              },
            }}
            onClick={() => navigate('/funcionalidades')}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 7 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />
                  </Box>
                  <Box>
                    <Chip
                      label="DIFERENCIAL COMPETITIVO"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        mb: 0.5,
                      }}
                    />
                    <Typography
                      variant="h4"
                      component="h3"
                      sx={{
                        fontWeight: 700,
                        color: '#FFFFFF',
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                      }}
                    >
                      {aiFeature.title}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.95)',
                    fontSize: '1.1rem',
                    lineHeight: 1.7,
                    mb: 2,
                  }}
                >
                  {aiFeature.subtitle} {aiFeature.description}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {aiFeature.bullets.map((bullet, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircleIcon sx={{ color: '#FCD34D', fontSize: 20 }} />
                      <Typography sx={{ color: '#FFFFFF', fontSize: '0.95rem', fontWeight: 500 }}>
                        {bullet}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* Regular Features Grid */}
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: 3,
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${landingColors.neutral.gray200}`,
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: landingShadows.card,
                      transform: 'translateY(-4px)',
                      borderColor: landingColors.primary.light,
                    },
                  }}
                  onClick={() => navigate('/funcionalidades')}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        bgcolor: alpha(landingColors.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: landingColors.primary.main,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Chip
                      label={feature.tag}
                      size="small"
                      sx={{
                        bgcolor: alpha(feature.tagColor, 0.1),
                        color: feature.tagColor,
                        fontWeight: 500,
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    component="h4"
                    sx={{
                      fontWeight: 600,
                      color: landingColors.neutral.gray900,
                      mb: 1,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: landingColors.neutral.gray600,
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button
              variant="outlined"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/funcionalidades')}
              sx={{
                borderColor: landingColors.primary.main,
                color: landingColors.primary.main,
                fontWeight: 500,
                px: 4,
                '&:hover': {
                  borderColor: landingColors.primary.dark,
                  bgcolor: alpha(landingColors.primary.main, 0.04),
                },
              }}
            >
              Ver Todas as Funcionalidades
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* PRICING SECTION */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: landingColors.background.alt }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 2,
            }}
          >
            Escolha o plano ideal para seu momento
          </Typography>
          <Typography
            align="center"
            sx={{
              color: landingColors.neutral.gray600,
              fontSize: '1.1rem',
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Sem surpresas. Sem taxas escondidas. Cancele quando quiser.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {['individual', 'small_clinic', 'clinic_pro', 'enterprise'].map((planId) => {
              const plan = PLAN_DEFINITIONS[planId as keyof typeof PLAN_DEFINITIONS];
              if (!plan) return null;

              const isPopular = plan.isPopular;

              return (
                <Grid key={planId} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: '#FFFFFF',
                      border: isPopular
                        ? `3px solid ${landingColors.primary.main}`
                        : `1px solid ${landingColors.neutral.gray200}`,
                      boxShadow: isPopular ? landingShadows.xl : landingShadows.card,
                      position: 'relative',
                      overflow: 'visible',
                      transform: isPopular ? { md: 'scale(1.05)' } : 'none',
                      zIndex: isPopular ? 2 : 1,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: landingShadows.xl,
                        transform: isPopular
                          ? { xs: 'translateY(-8px)', md: 'scale(1.05) translateY(-8px)' }
                          : 'translateY(-8px)',
                      },
                    }}
                  >
                    {isPopular && (
                      <Chip
                        icon={<StarIcon sx={{ fontSize: 14 }} />}
                        label="Mais Popular"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -14,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: landingColors.primary.main,
                          color: landingColors.neutral.white,
                          fontWeight: 700,
                          py: 2,
                          '& .MuiChip-icon': { color: landingColors.accent.yellow },
                        }}
                      />
                    )}
                    <CardContent sx={{ p: 3 }}>
                      {/* Plan Icon and Name */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: alpha(plan.colors.badge, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: plan.colors.badge,
                          }}
                        >
                          {planIcons[planId]}
                        </Box>
                        <Typography
                          variant="h6"
                          component="h4"
                          sx={{
                            fontWeight: 700,
                            color: landingColors.neutral.gray900,
                          }}
                        >
                          {plan.displayName}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: landingColors.neutral.gray500 }}>
                            R$
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '2.5rem',
                              fontWeight: 800,
                              color: plan.colors.badge,
                              lineHeight: 1,
                            }}
                          >
                            {Math.floor(plan.price)}
                          </Typography>
                          <Typography sx={{ fontSize: '1.2rem', fontWeight: 600, color: plan.colors.badge }}>
                            ,{(plan.price % 1).toFixed(2).substring(2)}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            color: landingColors.neutral.gray500,
                            fontSize: '0.875rem',
                          }}
                        >
                          por mês
                        </Typography>
                      </Box>

                      <Stack spacing={1.5} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon
                            sx={{ fontSize: 18, color: landingColors.accent.teal }}
                          />
                          <Typography sx={{ fontSize: '0.85rem', color: landingColors.neutral.gray700 }}>
                            {plan.limits.maxUsers === -1
                              ? 'Usuários ilimitados'
                              : `${plan.limits.maxUsers} usuário${plan.limits.maxUsers > 1 ? 's' : ''}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon
                            sx={{ fontSize: 18, color: landingColors.accent.teal }}
                          />
                          <Typography sx={{ fontSize: '0.85rem', color: landingColors.neutral.gray700 }}>
                            {plan.limits.maxClients === -1
                              ? 'Pacientes ilimitados'
                              : `Até ${plan.limits.maxClients} pacientes`}
                          </Typography>
                        </Box>
                        {plan.features.whatsappNotifications && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon
                              sx={{ fontSize: 18, color: landingColors.accent.teal }}
                            />
                            <Typography sx={{ fontSize: '0.85rem', color: landingColors.neutral.gray700 }}>
                              Lembretes WhatsApp
                            </Typography>
                          </Box>
                        )}
                        {plan.features.videoCall && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon
                              sx={{ fontSize: 18, color: landingColors.accent.teal }}
                            />
                            <Typography sx={{ fontSize: '0.85rem', color: landingColors.neutral.gray700 }}>
                              Teleconsulta
                            </Typography>
                          </Box>
                        )}
                        {plan.features.aiTranscription && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon
                              sx={{ fontSize: 18, color: landingColors.accent.teal }}
                            />
                            <Typography sx={{ fontSize: '0.85rem', color: landingColors.neutral.gray700 }}>
                              Transcrição IA
                            </Typography>
                          </Box>
                        )}
                        {plan.features.aiInsights && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon
                              sx={{ fontSize: 18, color: landingColors.accent.teal }}
                            />
                            <Typography sx={{ fontSize: '0.85rem', color: landingColors.neutral.gray700 }}>
                              Insights IA
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      <Button
                        fullWidth
                        variant={isPopular ? 'contained' : 'outlined'}
                        onClick={() => navigate('/planos')}
                        sx={{
                          py: 1.5,
                          fontWeight: 600,
                          ...(isPopular
                            ? {
                                bgcolor: landingColors.primary.main,
                                color: '#FFFFFF',
                                '&:hover': { bgcolor: landingColors.primary.dark },
                              }
                            : {
                                borderColor: landingColors.primary.main,
                                color: landingColors.primary.main,
                                '&:hover': {
                                  borderColor: landingColors.primary.dark,
                                  bgcolor: alpha(landingColors.primary.main, 0.04),
                                },
                              }),
                        }}
                      >
                        Ver Detalhes
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography sx={{ color: landingColors.neutral.gray600, mb: 2 }}>
              Todos os planos incluem: Suporte por email, Atualizações gratuitas, Backup automático
            </Typography>
            <Button
              variant="text"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/planos')}
              sx={{
                color: landingColors.primary.main,
                fontWeight: 500,
              }}
            >
              Comparar Planos Detalhadamente
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* TESTIMONIALS SECTION */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 2,
            }}
          >
            O que nossos usuários dizem
          </Typography>
          <Typography
            align="center"
            sx={{
              color: landingColors.neutral.gray600,
              fontSize: '1.1rem',
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Histórias reais de profissionais que transformaram suas clínicas
          </Typography>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid key={index} size={{ xs: 12, md: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    p: 4,
                    borderRadius: 3,
                    bgcolor: '#FFFFFF',
                    border: `1px solid ${landingColors.neutral.gray200}`,
                    boxShadow: 'none',
                    position: 'relative',
                  }}
                >
                  <FormatQuoteIcon
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontSize: 40,
                      color: alpha(landingColors.primary.main, 0.1),
                    }}
                  />

                  <Rating
                    value={testimonial.rating}
                    readOnly
                    size="small"
                    sx={{ mb: 2 }}
                    icon={<StarIcon sx={{ color: landingColors.accent.yellow }} />}
                    emptyIcon={<StarIcon />}
                  />

                  <Typography
                    sx={{
                      color: landingColors.neutral.gray700,
                      fontSize: '1rem',
                      lineHeight: 1.7,
                      mb: 3,
                      fontStyle: 'italic',
                    }}
                  >
                    "{testimonial.text}"
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={testimonial.photoUrl}
                      alt={testimonial.name}
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: landingColors.primary.main,
                      }}
                    >
                      {testimonial.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: landingColors.neutral.gray900,
                          fontSize: '0.95rem',
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: landingColors.neutral.gray500,
                          fontSize: '0.8rem',
                        }}
                      >
                        {testimonial.role}
                      </Typography>
                      <Typography
                        sx={{
                          color: landingColors.neutral.gray400,
                          fontSize: '0.75rem',
                        }}
                      >
                        {testimonial.location}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* FAQ SECTION */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: landingColors.background.alt }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 2,
            }}
          >
            Dúvidas frequentes
          </Typography>
          <Typography
            align="center"
            sx={{
              color: landingColors.neutral.gray600,
              fontSize: '1.1rem',
              mb: 6,
            }}
          >
            Tudo que você precisa saber antes de começar
          </Typography>

          {faqItems.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expandedFaq === `faq-${index}`}
              onChange={handleFaqChange(`faq-${index}`)}
              sx={{
                mb: 2,
                bgcolor: '#FFFFFF',
                borderRadius: '12px !important',
                border: `1px solid ${landingColors.neutral.gray200}`,
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: '0 0 16px 0',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 3,
                  '& .MuiAccordionSummary-content': {
                    my: 2,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: landingColors.neutral.gray900,
                  }}
                >
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Typography
                  sx={{
                    color: landingColors.neutral.gray600,
                    lineHeight: 1.7,
                  }}
                >
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* REFERRAL SECTION - Indique e Ganhe */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Card
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, #1B6A63 0%, #145A54 50%, #0F4A45 100%)',
              border: 'none',
              boxShadow: '0 20px 40px rgba(27, 106, 99, 0.3)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -80,
                left: -80,
                width: 250,
                height: 250,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 7 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CardGiftcardIcon sx={{ fontSize: 32, color: landingColors.accent.yellow }} />
                  </Box>
                  <Box>
                    <Chip
                      label="PROGRAMA DE INDICAÇÃO"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        mb: 0.5,
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: '#FFFFFF',
                        fontSize: { xs: '1.75rem', md: '2.25rem' },
                      }}
                    >
                      Indique e Ganhe
                    </Typography>
                    <Chip
                      label="Disponível nos planos Clínica Pro e Enterprise"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.12)',
                        color: 'rgba(255,255,255,0.75)',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        mt: 0.5,
                        // Em telas estreitas (≤320px) o texto quebra em vez de estourar
                        height: 'auto',
                        maxWidth: '100%',
                        '& .MuiChip-label': { whiteSpace: 'normal', display: 'block', py: 0.4, lineHeight: 1.3 },
                      }}
                    />
                  </Box>
                </Box>

                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '1.15rem',
                    lineHeight: 1.7,
                    mb: 3,
                  }}
                >
                  Conhece outro profissional de saúde mental que precisa organizar a clínica?
                  Indique a Clinafy e <strong>ganhe 1 mês grátis</strong> para cada indicação que se tornar cliente!
                </Typography>

                <Stack spacing={2} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ShareIcon sx={{ fontSize: 18, color: landingColors.accent.yellow }} />
                    </Box>
                    <Typography sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                      Compartilhe seu link exclusivo com colegas
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 18, color: landingColors.accent.yellow }} />
                    </Box>
                    <Typography sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                      Quando seu indicado assinar, você ganha 1 mês grátis
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <EmojiEventsIcon sx={{ fontSize: 18, color: landingColors.accent.yellow }} />
                    </Box>
                    <Typography sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                      Sem limite de indicações - ganhe meses ilimitados!
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    trackTrialStart('referral_section');
                    navigate(buildSignupUrl('/signup', { trial: 'true', ref: 'landing' }));
                  }}
                  sx={{
                    bgcolor: landingColors.accent.yellow,
                    color: landingColors.neutral.gray900,
                    fontWeight: 700,
                    fontSize: '1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: '#E5B800',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Criar Conta e Começar a Indicar
                </Button>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 4,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.9rem',
                      mb: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  >
                    Indicações convertidas
                  </Typography>
                  <Typography
                    sx={{
                      color: landingColors.accent.yellow,
                      fontSize: '4rem',
                      fontWeight: 800,
                      lineHeight: 1,
                      mb: 1,
                    }}
                  >
                    +200
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: '1rem',
                    }}
                  >
                    meses grátis distribuídos
                  </Typography>

                  <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.2)' }} />

                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.85rem',
                      fontStyle: 'italic',
                    }}
                  >
                    "Já indiquei 5 colegas e ganhei 5 meses grátis. O sistema se paga sozinho!"
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '0.75rem',
                      mt: 1,
                    }}
                  >
                    — Dra. Camila, Psicóloga
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* Badges de Segurança */}
      <Box sx={{ py: 4, backgroundColor: 'rgba(27, 106, 99, 0.03)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            {[
              { icon: <ShieldIcon />, label: 'LGPD', sublabel: 'Em conformidade' },
              { icon: <LockIcon />, label: 'AES-256', sublabel: 'Criptografia' },
              { icon: <CloudIcon />, label: 'AWS Brasil', sublabel: 'Servidores' },
              { icon: <BackupIcon />, label: 'Backup', sublabel: 'Automático' },
            ].map((badge, index) => (
              <Grid size={{ xs: 6, sm: 3 }} key={index}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '50%',
                    backgroundColor: 'rgba(27, 106, 99, 0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#1B6A63'
                  }}>
                    {badge.icon}
                  </Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">{badge.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{badge.sublabel}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* FINAL CTA SECTION */}
      {/* ================================================================== */}
      <Box
        sx={{
          background: landingGradients.heroDark,
          py: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            sx={{
              color: landingColors.neutral.white,
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2,
            }}
          >
            Pronto para transformar sua prática clínica?
          </Typography>
          <Typography
            sx={{
              color: alpha(landingColors.neutral.white, 0.8),
              fontSize: '1.1rem',
              mb: 4,
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            Junte-se a centenas de profissionais que já economizam tempo e atendem melhor com a Clinafy.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={() => {
              trackTrialStart('final_cta');
              navigate(buildSignupUrl('/signup', { trial: 'true' }));
            }}
            sx={{
              bgcolor: landingColors.neutral.white,
              color: landingColors.primary.main,
              fontWeight: 600,
              fontSize: '1.1rem',
              px: 5,
              py: 1.5,
              borderRadius: 2,
              boxShadow: landingShadows.button,
              '&:hover': {
                bgcolor: landingColors.neutral.gray100,
                boxShadow: landingShadows.buttonHover,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Começar Teste Gratuito de 30 Dias
          </Button>

          <Typography
            sx={{
              color: alpha(landingColors.neutral.white, 0.6),
              fontSize: '0.875rem',
              mt: 2,
            }}
          >
            Não precisa cartão de crédito. Cancele quando quiser.
          </Typography>
        </Container>
      </Box>

      </Box>

      <Footer />

      {/* Floating Lead Magnet Button */}
      <Box
        onClick={() => setLeadMagnetOpen(true)}
        sx={{
          position: 'fixed',
          bottom: { xs: 80, md: 24 },
          left: 24,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: '#1B6A63',
          color: 'white',
          py: 1.5,
          px: 2.5,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(27, 106, 99, 0.4)',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 25px rgba(27, 106, 99, 0.5)',
          },
        }}
      >
        <PictureAsPdfIcon sx={{ fontSize: 20 }} />
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
          PDF Grátis
        </Typography>
      </Box>

      {/* Lead Magnet Modal */}
      <LeadMagnetModal
        open={leadMagnetOpen}
        onClose={() => setLeadMagnetOpen(false)}
        magnetId="lgpd_checklist"
      />

      {/* Exit Intent Popup - Shows when user tries to leave */}
      <ExitIntentPopup delay={10000} />
    </Box>
  );
};

export default LandingPage;
